import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, Rocket, Sparkles, Wallet, ShieldCheck } from "lucide-react";

const STEPS = [
  {
    icon: Sparkles,
    title: "Create",
    body: "Name it (or let AI suggest one), pick a theme — AI drafts your logo, whitepaper and tokenomics in seconds.",
  },
  {
    icon: Rocket,
    title: "Publish",
    body: "From $19 your coin gets a live page with charts, tipping and share links. No wallet needed to publish.",
  },
  {
    icon: Wallet,
    title: "Mint on-chain",
    body: "One Phantom approval turns it into a real SPL token on Solana mainnet. Free devnet practice run included.",
  },
  {
    icon: ShieldCheck,
    title: "Rug-safe by default",
    body: "Mint + freeze authority are revoked automatically in the same transaction. No take-backs, no honeypots.",
  },
];

export default function Launchpad() {
  const [chain, setChain] = useState(null);

  useEffect(() => {
    api.get("/chains").then((r) => setChain(r.data.chains[0])).catch((e) => console.warn("chains load failed", e?.message));
  }, []);

  return (
    <section id="launchpad" className="border-t border-[#3a3a3a]" data-testid="section-launchpad">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-6 mb-12">
          <div className="col-span-12 lg:col-span-7">
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#cfff04] mb-3">
              02 // Launchpad
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-6xl uppercase tracking-tighter">
              Built on Solana.
              <br />
              Live in <span className="text-[#cfff04]">minutes.</span>
            </h2>
          </div>
          <p className="col-span-12 lg:col-span-5 self-end text-[#e2e2e2] text-sm">
            Every SolSpur coin is a real SPL token on Solana mainnet — no command line, no code,
            no seed-phrase juggling. One click in the app does it all.
          </p>
        </div>

        {/* Solana stats strip */}
        {chain ? (
          <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-[#3a3a3a] mb-8" data-testid="solana-stats">
            {[
              [`$${chain.launch_cost_usd}`, "On-chain cost"],
              [chain.launch_time, "Time to mainnet"],
              [chain.avg_fee_usd, "Avg network fee"],
              [chain.tps, "TPS"],
            ].map(([v, l]) => (
              <div key={l} className="min-w-0 border-r border-b border-[#3a3a3a] p-4 md:p-6">
                <dt className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#d0d0d0]">{l}</dt>
                <dd className="font-display text-xl sm:text-2xl lg:text-3xl font-bold mt-2 break-words">{v}</dd>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px mb-8" data-testid="solana-stats-skeleton">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="border border-[#3a3a3a] bg-[#121212] h-24 animate-pulse" />
            ))}
          </div>
        )}

        <div className="grid grid-cols-12 gap-3">
          {/* How it works — 4 steps */}
          <div className="col-span-12 lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="launchpad-steps">
            {STEPS.map(({ icon: Icon, title, body }, i) => (
              <div key={title} className="border border-[#3a3a3a] bg-[#121212] p-6" data-testid={`launchpad-step-${i}`}>
                <div className="flex items-center gap-3 mb-3">
                  <Icon size={16} className="text-[#cfff04]" />
                  <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#d0d0d0]">
                    Step 0{i + 1}
                  </span>
                </div>
                <div className="font-display text-lg font-bold uppercase mb-2">{title}</div>
                <p className="text-sm text-[#e2e2e2]">{body}</p>
              </div>
            ))}
          </div>

          {/* Why Solana + CTA */}
          <div className="col-span-12 lg:col-span-4 flex flex-col gap-3">
            <div className="border border-[#3a3a3a] bg-[#121212] p-6">
              <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#cfff04] mb-4">
                Why Solana
              </div>
              <ul className="space-y-2">
                {chain?.pros.map((p) => (
                  <li key={p} className="flex gap-2 text-sm text-[#e2e2e2]">
                    <Check size={16} className="text-[#00ff41] flex-shrink-0 mt-0.5" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>

            <div className="border border-[#cfff04] bg-[#121212] p-6 flex-1 flex flex-col justify-between" data-testid="launchpad-cta-card">
              <p className="text-sm text-[#e2e2e2] mb-5">
                AI logo + whitepaper, a live coin page, and one-click SPL minting with Phantom.
                From $19.
              </p>
              <Link
                to="/create"
                data-testid="launchpad-cta-create"
                className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-xs px-5 py-3 inline-flex items-center justify-center gap-2"
              >
                <Rocket size={14} /> Launch a Coin
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
